import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Search, FileText, Globe, Building2, Workflow, Settings, ArrowRight, Bot, Sparkles, BarChart3 } from 'lucide-react';
import { useBrand } from '../context/BrandContext';
import { getModKey } from '../hooks/useKeyboardShortcuts';

const POSTHOG_WORKFLOW_FLAG = 'experiment_new-module_workflow-builder_temp';
const POSTHOG_CHATBOT_FLAG = 'experiment_new-module_chatbot-builder_temp';

const STATIC_COMMANDS = [
  { id: 'forms', label: 'Forms', description: 'View and manage forms', icon: FileText, path: '/forms', feature: 'forms', category: 'Navigate' },
  { id: 'sites', label: 'Sites', description: 'View and manage sites & pages', icon: Globe, path: '/sites', feature: 'pages', category: 'Navigate' },
  { id: 'workflows', label: 'Workflows', description: 'View and manage workflows', icon: Workflow, path: '/workflows', feature: 'workflows', category: 'Navigate' },
  { id: 'data', label: 'Data', description: 'View and manage CRM data', icon: Building2, path: '/data/companies', feature: 'crm', category: 'Navigate' },
  { id: 'pipelines', label: 'Pipelines', description: 'View deal pipelines', icon: BarChart3, path: '/data/pipelines', feature: 'crm', category: 'Navigate' },
  { id: 'chatbots', label: 'Chatbots', description: 'View and manage chatbots', icon: Bot, path: '/chatbots', feature: 'chatbots', category: 'Navigate' },
  { id: 'chat', label: 'Assistant', description: 'Open the AI assistant', icon: Sparkles, path: '/chat', feature: 'chat', category: 'Navigate' },
  { id: 'settings', label: 'Settings', description: 'Account and organization settings', icon: Settings, path: '/settings', category: 'Navigate' },
];

export default function CommandPalette({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const { brandConfig } = useBrand();
  const workflowFlagEnabled = useFeatureFlagEnabled(POSTHOG_WORKFLOW_FLAG);
  const chatbotFlagEnabled = useFeatureFlagEnabled(POSTHOG_CHATBOT_FLAG);

  // Filter commands by brand features, PostHog flags, and search query
  const filteredCommands = useMemo(() => {
    const available = STATIC_COMMANDS.filter((cmd) => {
      if (cmd.feature && !brandConfig.features[cmd.feature]) return false;
      if (cmd.feature === 'workflows' && !workflowFlagEnabled) return false;
      if (cmd.feature === 'chatbots' && !chatbotFlagEnabled) return false;
      return true;
    });

    if (!query.trim()) return available;

    const q = query.toLowerCase();
    return available.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(q) ||
        cmd.description.toLowerCase().includes(q)
    );
  }, [query, brandConfig.features, workflowFlagEnabled, chatbotFlagEnabled]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      // Use rAF to avoid focus issues when another element is unmounting
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-command-item]');
    items[selectedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (command) => {
      onClose();
      if (command.path) {
        navigate(command.path);
      }
    },
    [navigate, onClose]
  );

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredCommands.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredCommands.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            handleSelect(filteredCommands[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, handleSelect, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[min(20vh,180px)] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm" />

      {/* Palette */}
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Search input */}
        <div className="flex items-center px-4 border-b border-gray-200 dark:border-gray-700">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search or jump to..."
            className="flex-1 px-3 py-3.5 text-[14px] text-gray-900 dark:text-white placeholder-gray-400 bg-transparent border-0 outline-none focus:ring-0"
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded font-mono">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[320px] overflow-y-auto py-2">
          {filteredCommands.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-[13px] text-gray-400 dark:text-gray-500">
                No results for &ldquo;{query}&rdquo;
              </p>
            </div>
          ) : (
            filteredCommands.map((cmd, index) => {
              const Icon = cmd.icon;
              const isSelected = index === selectedIndex;
              return (
                <button
                  key={cmd.id}
                  data-command-item
                  onClick={() => handleSelect(cmd)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    isSelected
                      ? 'bg-gray-100 dark:bg-gray-800'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${
                      isSelected
                        ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-gray-900 dark:text-white">
                      {cmd.label}
                    </div>
                    <div className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                      {cmd.description}
                    </div>
                  </div>
                  {isSelected && (
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Footer hints */}
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex items-center gap-4">
          <span className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
            <kbd className="inline-flex items-center justify-center w-4 h-4 text-[9px] bg-gray-200 dark:bg-gray-700 rounded">↑</kbd>
            <kbd className="inline-flex items-center justify-center w-4 h-4 text-[9px] bg-gray-200 dark:bg-gray-700 rounded">↓</kbd>
            Navigate
          </span>
          <span className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
            <kbd className="inline-flex items-center justify-center min-w-[24px] h-4 px-1 text-[9px] bg-gray-200 dark:bg-gray-700 rounded">Enter</kbd>
            Open
          </span>
          <span className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
            <kbd className="inline-flex items-center justify-center min-w-[24px] h-4 px-1 text-[9px] bg-gray-200 dark:bg-gray-700 rounded">{getModKey()}K</kbd>
            Toggle
          </span>
        </div>
      </div>
    </div>
  );
}
